import { DEBOUNCE_WAIT_TIME } from '@common/constants/global.constants'
import { Role } from '@common/enums/request.enums'
import { DashboardTrainee, FiltersFormValues } from '@common/types/person.types'
import Button from '@components/arthurBased/inputs/Button'
import Icon from '@components/dataDisplay/Icon'
import ClearableTextInput from '@components/inputs/ClearableTextInput'
import CompanyAutocomplete from '@components/inputs/CompanyAutocomplete'
import Loader from '@components/state/Loader'
import { GREY_COLORS, RED_COLORS } from '@config/theme/colors'
import { AddCircleOutlineRounded, AutorenewRounded, FilterAltRounded, IosShareRounded, SearchRounded } from '@mui/icons-material'
import { debounce, Divider, Stack, styled, Typography } from '@mui/material'
import { useCertificationApi } from '@services/api/certification.api'
import { useCompanyApi } from '@services/api/company.api'
import { usePersonApi } from '@services/api/person.api'
import { useAuth } from '@services/contexts/AuthContext'
import { useSnackbar } from '@services/contexts/SnackbarContext'
import { useTraineeListFilters } from '@services/contexts/TraineeListFiltersContext'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FormattedMessage, useIntl } from 'react-intl'
import AdvancedFiltersPopIn from './AdvancedFiltersPopin'
import TraineeCreationPopIn from './TraineeCreationPopIn'
import TraineeRow from './TraineeRow'

type GetTraineeValues = {
	page: number
	filters: FiltersFormValues
}

/**
 * Trainees
 * @constructor
 */
const TraineesList = () => {
	const { user } = useAuth()
	const { exportCertifications } = useCertificationApi()
	const { getCompanies } = useCompanyApi()
	const { formatMessage } = useIntl()
	const { getTrainees } = usePersonApi()
	const { addSnackbar } = useSnackbar()
	const { filters: savedFilters, setFilters, companyLabel, setCompanyLabel } = useTraineeListFilters()
	const [hasMore, setHasMore] = useState(true)
	const [isAdvancedFiltersPopInOpen, setIsAdvancedFiltersPopInOpen] = useState(false)
	const [isLoading, setIsLoading] = useState(true)
	const [isTraineeCreationPopInOpen, setIsTraineeCreationPopInOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [trainees, setTrainees] = useState<DashboardTrainee[]>([])
	const prevFiltersRef = useRef<FiltersFormValues | null>(null)

	const {
		control,
		watch,
		reset,
		setValue
	} = useForm<FiltersFormValues>({ defaultValues: savedFilters })

	const filters = watch()

	const hasRedDot = filters.course !== '' || !!filters.start || !!filters.end

	const { mutate: getTraineesMutate, isPending: isTraineesPending } = useMutation<DashboardTrainee[], Error, GetTraineeValues>({
		mutationKey: ['getTrainees'],
		mutationFn: ({ page, filters }) => getTrainees(page, filters),
		onSuccess: (data, { page: newPage, filters: newFilters }) => {
			setPage(newPage)
			setFilters(newFilters)
			setHasMore(data.length === 50)
			setTrainees(previous => {
				const previousMap = newPage > 1
					? new Map(previous.map((item) => [item.id, item]))
					: new Map()

				data.forEach(item => {
					const previousItem = previousMap.get(item.id)
					previousMap.set(item.id, { ...previousItem, ...item })
				})

				return Array.from(previousMap.values())
			})
		},
		onError: (e) => {
			setHasMore(false)
			addSnackbar(e.message, 'error')
		},
		onSettled: () => {
			// If filters changed only
			if (isLoading) {
				setIsLoading(false)
			}
		}
	})

	const { mutate: exportCertificationsMutate, isPending: isExportLoading } = useMutation({
		mutationKey: ['exportCertifications'],
		mutationFn: () => exportCertifications()
	})

	const { data: companies } = useQuery({
		queryKey: ['getCompanies', isTraineeCreationPopInOpen],
		queryFn: () => getCompanies()
	})

	const debouncedMutate = useCallback(debounce((variables) => getTraineesMutate(variables), DEBOUNCE_WAIT_TIME), [])

	useEffect(() => getTraineesMutate({ page: 1, filters }), [])

	useEffect(() => {
		if (JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters)) {
			prevFiltersRef.current = filters

			setIsLoading(true)
			setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0)
			debouncedMutate({ page: 1, filters })
		}
	}, [filters])

	return (
		<Container>
			<Header>
				<Filters>
					<InputFilters>
						<CompanyFilter>
							<CompanyAutocomplete
								control={control}
								name="company"
								placeholder={formatMessage({ id: 'dashboard.trainees.filters.company' })}
								onChange={(option) => setCompanyLabel(option?.label ?? '')}
								companies={companies}
							/>
						</CompanyFilter>
						<ClearableTextInput
							control={control}
							name="query"
							placeholder={formatMessage({ id: 'dashboard.trainees.filters.query' })}
							startAdornment={<Icon color={GREY_COLORS[500]} IconElement={SearchRounded} />}
						/>
						<ButtonFilters>
							<IconButtonContent className={hasRedDot ? 'active' : ''} onClick={() => setIsAdvancedFiltersPopInOpen(true)}>
								<Icon IconElement={FilterAltRounded} />
							</IconButtonContent>
							<IconButtonContent onClick={() => reset(
								{
									query: '',
									company: '',
									course: '',
									start: undefined,
									end: undefined
								}
							)}>
								<Icon IconElement={AutorenewRounded} />
							</IconButtonContent>
						</ButtonFilters>
					</InputFilters>
					{
						(!!user && user.role === Role.ADMIN) && (
							<Button variant="tertiary" onClick={() => exportCertificationsMutate()} disabled={isExportLoading}>
								{
									isExportLoading
										? <Loader />
										: (
											<ButtonContent>
												<Typography>
													<FormattedMessage id="global.buttons.export" />
												</Typography>
												<Icon IconElement={IosShareRounded} />
											</ButtonContent>
										)
								}
							</Button>
						)
					}
				</Filters>
				<Button onClick={() => setIsTraineeCreationPopInOpen(true)}>
					<ButtonContent>
						<Typography>
							<FormattedMessage id="dashboard.trainees.actions.addTrainee.label" />
						</Typography>
						<Icon IconElement={AddCircleOutlineRounded} />
					</ButtonContent>
				</Button>
			</Header>
			{
				isLoading
					? <Loader />
					: (
						<ListContainer>
							{/* <Tooltip title={trainees?.length ? formatMessage({ id: 'dashboard.tooltips.viewTraineeDetails' }) : null} placement="bottom-start" followCursor> */}
							<List id="scrollableContainer">
								{
									trainees?.length
										? (
											<InfiniteScroll
												dataLength={trainees.length}
												hasMore={hasMore && !isTraineesPending}
												loader={<Loader />}
												next={() => {
													if (isTraineesPending) return
													getTraineesMutate({ page: page + 1, filters })
												}}
												scrollableTarget="scrollableContainer"
												style={{ overflow: 'hidden' }}
											>
												{trainees.map((trainee, index) => (
													<Stack key={trainee.id}>
														{index !== 0 && <Divider />}
														<TraineeRow trainee={trainee} onCertificationCreated={() => getTraineesMutate({ page: 1, filters })} />
													</Stack>
												))}
											</InfiniteScroll>
										)
										: (
											<NoData>
												<Typography>
													<FormattedMessage id="dashboard.trainees.list.noData" />
												</Typography>
											</NoData>
										)
								}
							</List>
							{/* </Tooltip> */}
						</ListContainer>
					)
			}
			{isTraineeCreationPopInOpen && (
				<TraineeCreationPopIn
					onClose={() => setIsTraineeCreationPopInOpen(false)}
					onSuccess={() => getTraineesMutate({ page: 1, filters })}
					companyId={filters.company}
					companyLabel={companyLabel}
					setCompanyLabel={setCompanyLabel}
				/>
			)}
			{isAdvancedFiltersPopInOpen && (
				<AdvancedFiltersPopIn
					initialValues={{
						course: filters.course,
						start: filters.start,
						end: filters.end
					}}
					onClose={() => setIsAdvancedFiltersPopInOpen(false)}
					onConfirm={(advancedFilters) => {
						setValue('course', advancedFilters.course)
						setValue('start', advancedFilters.start)
						setValue('end', advancedFilters.end)
						setIsAdvancedFiltersPopInOpen(false)
					}}
				/>
			)}
		</Container>
	)
}

const Container = styled(Stack)({
	flex: 1,
	rowGap: '16px'
})

const Header = styled(Stack)({
	rowGap: '16px'
})

const Filters = styled(Stack)({
	columnGap: '16px',
	flexDirection: 'row',
	justifyContent: 'space-between'
})

const InputFilters = styled(Stack)({
	columnGap: '16px',
	flexDirection: 'row',
	width: '60%'
})

// 50% wider than the search field next to it
const CompanyFilter = styled(Stack)({
	flex: 1.5
})

const ButtonFilters = styled(Stack)({
	columnGap: '8px',
	flexDirection: 'row'
})

const List = styled(Stack)({
	flex: 1,
	overflow: 'auto'
})

const ListContainer = styled(Stack)({
	border: `1px solid ${GREY_COLORS[200]}`,
	borderRadius: '8px',
	flex: 1,
	overflow: 'hidden'
})

const NoData = styled(Stack)({
	alignItems: 'center',
	justifyContent: 'center',
	flex: 1
})

const ButtonContent = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row',
	justifyContent: 'center'
})

const IconButtonContent = styled(Stack)({
	alignItems: 'center',
	columnGap: '4px',
	flexDirection: 'row',
	justifyContent: 'center',
	position: 'relative',
	width: '40px',
	cursor: 'pointer',
	'&.active::after': {
		content: '""',
		position: 'absolute',
		top: 0,
		right: 0,
		width: '8px',
		height: '8px',
		backgroundColor: RED_COLORS[700],
		borderRadius: '100px'
	}
})

export default TraineesList
